import { css } from '@emotion/react';
import { Button, Page, Divider } from '@frontend/generic-dialpad-accessories';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { CallInformation } from '../components/call-information';
import { PageHeaderWraps } from '../components/generic/page-header-wraps';
import { HeldCall } from '../components/hold/held-call';
import { MergedCallGroupHeldCall } from '../components/hold/merged-call-group-held-call';
import { useSoftphoneCallState } from '../providers/softphone-call-state-provider';
import { useSoftphoneCallActions } from '../providers/softphone-call-state-provider/softphone-call-actions.provider';
import { useSoftphoneEventsEmitter } from '../providers/softphone-events-provider';
import { useSoftphoneRouter } from '../providers/softphone-router-provider';
import { isEstablishedCall, isTerminatedCall } from '../types';

export const PageCallHome = () => {
  const { t } = useTranslation('softphone');
  const primaryCall = useSoftphoneCallState((ctx) => ctx.primaryCall);
  const establishedCalls = useSoftphoneCallState((ctx) => ctx.establishedCalls);
  const setCurrentRoute = useSoftphoneRouter((ctx) => ctx.setSingleCallRoute);
  const disabled = !primaryCall || !isEstablishedCall(primaryCall) || isTerminatedCall(primaryCall);
  const mergedCallIds = useSoftphoneCallState((ctx) => ctx.mergedCallIds);
  const mergedCallGroup = useSoftphoneCallState((ctx) => ctx.mergedCallGroup);
  const isMergedCallPrimaryCall = useSoftphoneCallState((ctx) => ctx.isMergedCallPrimaryCall);

  const otherHeldCalls = establishedCalls.filter(
    (call) => !!call.onHoldSince && primaryCall?.id !== call.id && !mergedCallIds?.includes(call.id)
  );
  const mergeCalls = useSoftphoneCallActions((ctx) => ctx.mergeCalls);
  const canMerge = establishedCalls.length > 1;
  const { emit } = useSoftphoneEventsEmitter();

  const handleTransfer = () => {
    setCurrentRoute('transfer');
    const call = establishedCalls.find((call) => call.id === primaryCall?.id);
    if (call) emit('transfer-call.initiated', { call });
  };

  return (
    <Page>
      <PageHeaderWraps hasBackButton={false}>
        <CallInformation
          displayName={primaryCall?.remoteParty.displayName}
          uri={primaryCall?.remoteParty.uri ?? ''}
          hideCallDuration
        />
      </PageHeaderWraps>
      <Page.Body
        css={css`
          display: flex;
          justify-content: space-around;
          align-items: center;
          margin: ${theme.spacing(3, 0)};
        `}
      >
        <Button.Round
          disabled={disabled}
          onClick={() => setCurrentRoute('park')}
          size='medium'
          iconName={'phone-parked-calls'}
          label={t('Park')}
        ></Button.Round>
        <Button.Round
          trackingId='softphone-active-transfer'
          disabled={disabled}
          onClick={handleTransfer}
          size='medium'
          iconName={'phone-outgoing'}
          label={t('Transfer')}
        ></Button.Round>
        {canMerge && (
          <Button.Round
            disabled={disabled}
            onClick={() => {
              mergeCalls(establishedCalls);
            }}
            size='medium'
            iconName={'merge'}
            label={t('Merge')}
          ></Button.Round>
        )}
        <Button.Round
          trackingId='softphone-active-keypad'
          disabled={disabled}
          onClick={() => setCurrentRoute('keypad')}
          size='medium'
          iconName={'dial'}
          label={t('Keypad')}
        ></Button.Round>
        <Button.Round
          disabled={disabled}
          onClick={() => {
            setCurrentRoute('add-call');
          }}
          size='medium'
          iconName={'plus'}
          label={t('Add Call')}
        ></Button.Round>
      </Page.Body>
      <Page.Footer>
        {(!!otherHeldCalls.length || (!isMergedCallPrimaryCall && !!mergedCallGroup?.length)) && (
          <>
            <Divider />
            <Text
              color='white'
              css={css`
                margin-bottom: ${theme.spacing(1)};
              `}
            >
              {t('On Local Hold')}
            </Text>
            {!!mergedCallGroup?.length && <MergedCallGroupHeldCall />}
            {otherHeldCalls.map((call) => (
              <HeldCall key={call.id} call={call}></HeldCall>
            ))}
          </>
        )}
      </Page.Footer>
    </Page>
  );
};
